import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, inject, Input, OnInit, signal } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { UpdatePlanFieldCodes } from '@app/core/constants/payment-plan.const';
import { PaymentPlanDataService } from '@app/core/data-services/payment-plan/payment-plan-data.service';
import { UiButtonComponent } from '@app/shared/elements/ui-button/ui-button.component';
import { SharedModule } from '@app/shared/shared.module';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { ToastrService } from 'ngx-toastr';
import { take } from 'rxjs/operators';

export enum CommentPropertyName {
  DDR_PLANS = 'custrecord_mfa_ddr_provider_comments',
  PENDING_PLANS = 'custrecord_unsubmitted_comment'
}

@Component({
  selector: 'sliqpay-comment-modal',
  standalone: true,
  imports: [CommonModule, SharedModule, ReactiveFormsModule, UiButtonComponent],
  templateUrl: './comment-modal.component.html',
  styleUrls: ['./comment-modal.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class CommentModalComponent implements OnInit {
  CONFIG_KEY = 'plan_comment';
  private readonly fieldCode = UpdatePlanFieldCodes.COMMENTS;

  // Injections
  private readonly activeModal = inject(NgbActiveModal);
  private readonly paymentPlanDataService = inject(PaymentPlanDataService);
  private readonly toastrService = inject(ToastrService);

  //  inputs
  @Input() commentValue = null;
  @Input() id = '';
  @Input() propertyName: CommentPropertyName = CommentPropertyName.DDR_PLANS;

  // Loading state
  readonly loading = signal(false);

  // Form
  readonly form = new FormGroup({
    comments: new FormControl<string | null>(null)
  });

  ngOnInit(): void {
    this.form.get('comments')?.setValue(this.commentValue);
  }

  saveComment(): void {
    if (this.form.invalid) return;

    this.loading.set(true);

    const payload = {
      fieldCode: this.fieldCode,
      [this.propertyName]: this.form.get('comments')?.getRawValue()
    };

    this.paymentPlanDataService
      .saveComment$(this.id, payload, this.propertyName)
      .pipe(take(1))
      .subscribe({
        next: response => {
          this.toastrService.success(response.message, 'Comment');
          this.closeModal(payload);
        },
        error: () => this.loading.set(false),
        complete: () => this.loading.set(false)
      });
  }

  closeModal(result?: unknown): void {
    this.activeModal.close(result);
  }
}
